<template>
  <div class="paper-detail container py-4">

    <!-- 載入中 -->
    <div v-if="loading" class="text-center py-5">
      文章載入中…
    </div>

    <!-- 錯誤訊息 -->
    <div v-else-if="errorMsg" class="alert alert-danger">
      {{ errorMsg }}
    </div>

    <!-- 有文章資料 -->
    <div v-else-if="article">
      <!-- 返回列：獨立一行，靠左 -->
      <div class="detail-header mb-2">
        <button
          type="button"
          class="btn btn-link ps-0 detail-back-btn"
          @click="goBack"
        >
          ‹ 返回精選分享
        </button>
      </div>

      <!-- 標題 + 日期：同一行，標題在左、日期在右 -->
      <div class="detail-title-row d-flex justify-content-between align-items-end mb-4">
        <h2 class="fw-bold text-primary detail-title mb-0">
          {{ article.title }}
        </h2>
        <p class="text-muted small mb-0 detail-date">
          發佈日期：{{ formatDate(article.published_at) }}
        </p>
      </div>

      <!-- Markdown 內容 -->
      <div
        class="markdown-body"
        v-html="htmlContent"
      ></div>
    </div>

    <!-- 找不到 -->
    <div v-else class="text-muted text-center py-5">
      找不到這篇文章。
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { supabase } from '@/lib/supabase'
import MarkdownIt from 'markdown-it'

// HackMD 風格的額外語法
import markdownItMark from 'markdown-it-mark'          // 支援 ==標記==
import markdownItContainer from 'markdown-it-container' // 支援 :::info

export default {
  name: 'PaperDetail',
  setup () {
    const route = useRoute()
    const router = useRouter()

    const loading = ref(true)
    const errorMsg = ref('')
    const article = ref(null)
    const htmlContent = ref('')

    // 建立 markdown-it 實例
    const md = new MarkdownIt({
      html: true,
      linkify: true,
      breaks: true
    })

    // 啟用 ==highlight== 語法
    md.use(markdownItMark)

    // 啟用 :::info 區塊
    md.use(markdownItContainer, 'info', {
      render (tokens, idx) {
        const token = tokens[idx]
        if (token.nesting === 1) {
          return '<div class="md-info-box">\n'
        } else {
          return '</div>\n'
        }
      }
    })

    const formatDate = (value) => {
      if (!value) return '-'
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return value
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}/${m}/${day}`
    }

    const goBack = () => {
      router.push({ name: 'AppPapers' })
    }

    const cleanMarkdown = (text) => {
      // 去 BOM + 統一換行
      text = (text || '').replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n')

      // 移除 YAML frontmatter
      text = text.replace(/^---[\s\S]*?---\s*/m, '')

      const lines = text.split('\n').filter(line => {
        const trimmed = line.trim()
        if (/^title\s*:/.test(trimmed)) return false
        if (/^cover\s*:/.test(trimmed)) return false
        return true
      })

      return lines.join('\n')
    }

    const autoFenceVueLikeBlocks = (text) => {
      if (!text) return text

      // 已經有 code fence 就不要亂包
      if (/```/.test(text)) return text

      // 偵測 Vue SFC 或大量 HTML 標籤（很像你貼的那種）
      const looksLikeVueSFC =
        /<template[\s>]/i.test(text) &&
        (/<script[\s>]/i.test(text) || /<style[\s>]/i.test(text))

      if (looksLikeVueSFC) {
        return text.replace(
          /(<template[\s\S]*$)/i,
          '```vue\n$1\n```'
        )
      }

      return text
    }


    const rewriteImagePaths = (text, contentPath) => {
      if (!contentPath) return text
      const dir = contentPath.replace(/\/[^/]+$/, '')

      return text.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (match, alt, src) => {
        const trimmed = src.trim()
        if (/^https?:\/\//i.test(trimmed)) return match

        const cleanedPath = trimmed.replace(/^\.\/+/, '').replace(/^\.\\+/, '')
        const fullPath = dir ? `${dir}/${cleanedPath}` : cleanedPath

        const { data } = supabase
          .storage
          .from('papers')
          .getPublicUrl(fullPath)

        if (!data?.publicUrl) return match
        return `![${alt}](${data.publicUrl})`
      })
    }

    const loadArticle = async () => {
      loading.value = true
      errorMsg.value = ''
      article.value = null
      htmlContent.value = ''

      try {
        const id = route.params.id
        if (!id) {
          errorMsg.value = '找不到指定文章。'
          return
        }

        const { data, error } = await supabase
          .from('papers')
          .select('*')
          .eq('id', id)
          .maybeSingle()

        if (error) {
          console.error('[PaperDetail] load article error', error)
          throw error
        }
        if (!data) {
          errorMsg.value = '找不到這篇文章。'
          return
        }

        article.value = data

        if (!data.content_path) {
          errorMsg.value = '此文章尚未設定內容檔案。'
          return
        }

        const { data: file, error: fileError } = await supabase
          .storage
          .from('papers')
          .download(data.content_path)

        if (fileError) {
          console.error('[PaperDetail] download md error', fileError)
          throw fileError
        }

        const rawText = await file.text()

        const noMeta = cleanMarkdown(rawText)
        const fenced = autoFenceVueLikeBlocks(noMeta)
        const withFixedImages = rewriteImagePaths(fenced, data.content_path)

        htmlContent.value = md.render(withFixedImages)

      } catch (err) {
        console.error('[PaperDetail] exception', err)
        errorMsg.value = '文章載入失敗，請稍後再試。'
      } finally {
        loading.value = false
      }
    }

    onMounted(loadArticle)

    return {
      loading,
      errorMsg,
      article,
      htmlContent,
      formatDate,
      goBack
    }
  }
}
</script>

<style scoped>
/* 文章區：限制最大寬度並置中，避免佔滿 AppShell 中央區域 */
.paper-detail {
  max-width: 760px;
  margin: 0 auto;
}

/* 返回列：靠左、不要多餘間距 */
.detail-header {
  text-align: left;
}

.detail-back-btn {
  font-size: 0.9rem;
}

/* 標題 + 日期排版 */
.detail-title-row {
  text-align: left;
}

.detail-title {
  flex: 1 1 auto;
  margin-right: 1rem;
  word-break: break-word;
}

.detail-date {
  white-space: nowrap;
}

/* Markdown 外層排版 */
.markdown-body {
  line-height: 1.8;
  font-size: 0.95rem;
}

/* ✅ 用 :deep() 讓樣式打到 v-html 產生的元素 */

/* 整體文字靠左排 */
.markdown-body :deep(p),
.markdown-body :deep(ul),
.markdown-body :deep(ol),
.markdown-body :deep(li) {
  text-align: left;
}

/* 標題靠左 */
.markdown-body :deep(h1),
.markdown-body :deep(h2),
.markdown-body :deep(h3),
.markdown-body :deep(h4) {
  margin-top: 1.6rem;
  margin-bottom: 0.8rem;
  font-weight: 600;
  text-align: left;
}
.markdown-body :deep(h1) {
  font-size: 1.5rem;   /* 文章主標（如果你會用到） */
}

.markdown-body :deep(h2) {
  font-size: 1.3rem;   /* 內文大標，例如 ## 方法 */
}

.markdown-body :deep(h3) {
  font-size: 1.1rem;   /* ← 這就是 ### 小標題，用這裡調小 */
}

.markdown-body :deep(h4) {
  font-size: 1rem;     /* 更小一階的小標 */
}

.markdown-body :deep(p) {
  margin-bottom: 0.9rem;
}

.markdown-body :deep(ul),
.markdown-body :deep(ol) {
  padding-left: 1.4rem;
  margin-bottom: 0.9rem;
}

/* 圖片：寬度跟著文章寬度走，會隨視窗縮放 */
.markdown-body :deep(img) {
  display: block;
  width: 100%;
  max-width: 100%;
  height: auto;
  border-radius: 0.5rem;
  margin: 1.5rem auto;
}

/* 連結樣式 */
.markdown-body :deep(a) {
  color: #0d6efd;
  text-decoration: underline;
}

/* ==標記文字== → markdown-it-mark 會輸出 <mark> */
.markdown-body :deep(mark) {
  background-color: #fff3cd;
  padding: 0 0.15em;
  border-radius: 0.25rem;
}

/* :::info 區塊樣式（對應 md-info-box） */
.markdown-body :deep(.md-info-box) {
  margin: 1.5rem 0;
  padding: 1rem 1.25rem;
  border-radius: 0.5rem;
  border-left: 4px solid #0d6efd;
  background: #f3f7ff;
  color: #333;
  font-size: 0.95rem;
}

.markdown-body :deep(.md-info-box p:last-child) {
  margin-bottom: 0;
}

/* 表格：欄位強制左對齊 */
.markdown-body :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 1.2rem 0;
}

.markdown-body :deep(th),
.markdown-body :deep(td) {
  text-align: left !important;
  padding: 8px 12px;
  vertical-align: top;
}

.markdown-body :deep(th) {
  font-weight: 600;
  border-bottom: 2px solid #e5e5e5;
}

.markdown-body :deep(td) {
  border-bottom: 1px solid #f0f0f0;
}
</style>
